import { Injectable, inject } from '@angular/core';
import { Subject } from 'rxjs';

import { ApiUrlService } from '@pryspace/shared/environments';
import { AccessChangedEvent } from './models';
import { TokenStore } from './token-store.service';

@Injectable({ providedIn: 'root' })
export class AccessRealtimeService {
  private api = inject(ApiUrlService);
  private tokens = inject(TokenStore);

  readonly changes$ = new Subject<AccessChangedEvent>();

  private socket: WebSocket | null = null;
  private tenantSlug: string | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = true;
  private lastEventId: string | null = null;

  connect(tenantSlug: string): void {
    if (typeof WebSocket === 'undefined') return;
    if (this.tenantSlug === tenantSlug && this.socket
      && (this.socket.readyState === WebSocket.OPEN
        || this.socket.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.disconnect();
    this.tenantSlug = tenantSlug;
    this.stopped = false;
    this.open();
  }

  disconnect(): void {
    this.stopped = true;
    this.tenantSlug = null;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.socket?.close();
    this.socket = null;
  }

  private open(): void {
    const slug = this.tenantSlug;
    const token = this.tokens.getCustomerAccess();
    if (this.stopped || !slug || !token) return;

    const path = `/ws/customer/tenant/${encodeURIComponent(slug)}/access/`;
    try {
      const socket = new WebSocket(this.api.websocket(path), ['prypro.jwt', token]);
      this.socket = socket;
      socket.onmessage = (message) => this.handleMessage(message.data);
      socket.onclose = (event) => {
        if (this.socket === socket) this.socket = null;
        if ([4401, 4403, 4404].includes(event.code)) return;
        this.scheduleReconnect();
      };
      socket.onerror = () => socket.close();
    } catch {
      this.scheduleReconnect();
    }
  }

  private handleMessage(raw: unknown): void {
    if (typeof raw !== 'string') return;
    try {
      const event = JSON.parse(raw) as AccessChangedEvent;
      if (event.type !== 'access.changed') return;
      if (event.event_id && event.event_id === this.lastEventId) return;
      this.lastEventId = event.event_id ?? null;
      this.changes$.next(event);
    } catch {
      // Ignore malformed frames; the HTTP API remains authoritative.
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer || !this.tenantSlug) return;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.open();
    }, 2000);
  }
}
